import React, { FormEvent, useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import {
  FormSelectField,
  FormTextField,
} from "../../common/dashboard/FormFields";
import { ModalShell } from "../../common/dashboard/ModalShell";
import {
  wizardGoldButtonSx,
  wizardOutlinedButtonSx,
} from "../../common/dashboard/formStyles";
import {
  getProspectErrorMessage,
  PROSPECT_FIELD_MAX_LENGTHS,
} from "./prospectFormModel";

export type ProspectLeadCaptureOption = {
  id: string;
  label: string;
};

type LeadCaptureSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: ProspectLeadCaptureOption[];
  onCreateLeadCapture: (label: string) => Promise<ProspectLeadCaptureOption>;
  error?: string;
  loading?: boolean;
  disabled?: boolean;
};

const ADD_LEAD_CAPTURE_VALUE = "__add-new-lead-capture__";

export const LeadCaptureSelect = ({
  value,
  onChange,
  options,
  onCreateLeadCapture,
  error,
  loading = false,
  disabled = false,
}: LeadCaptureSelectProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newLabelError, setNewLabelError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [locallyCreatedOptions, setLocallyCreatedOptions] = useState<
    ProspectLeadCaptureOption[]
  >([]);

  const availableOptions = useMemo(() => {
    const optionsById = new Map<string, ProspectLeadCaptureOption>();
    [...options, ...locallyCreatedOptions].forEach((option) => {
      optionsById.set(option.id, option);
    });
    return Array.from(optionsById.values());
  }, [locallyCreatedOptions, options]);

  const closeModal = () => {
    if (isSaving) {
      return;
    }
    setModalOpen(false);
    setNewLabel("");
    setNewLabelError("");
  };

  const handleSelectChange = (nextValue: string) => {
    if (nextValue === ADD_LEAD_CAPTURE_VALUE) {
      setModalOpen(true);
      return;
    }
    onChange(nextValue);
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const trimmedLabel = newLabel.trim();

    if (!trimmedLabel) {
      setNewLabelError("Enter a lead capture name.");
      return;
    }
    if (trimmedLabel.length > PROSPECT_FIELD_MAX_LENGTHS.leadCaptureLabel) {
      setNewLabelError(
        `Lead capture name must be ${PROSPECT_FIELD_MAX_LENGTHS.leadCaptureLabel} characters or fewer.`,
      );
      return;
    }

    const duplicateExists = availableOptions.some(
      (option) =>
        option.label.trim().toLowerCase() === trimmedLabel.toLowerCase(),
    );
    if (duplicateExists) {
      setNewLabelError("A lead capture with this name already exists.");
      return;
    }

    setIsSaving(true);
    setNewLabelError("");
    try {
      const createdOption = await onCreateLeadCapture(trimmedLabel);
      setLocallyCreatedOptions((currentOptions) => [
        ...currentOptions,
        createdOption,
      ]);
      onChange(createdOption.id);
      setModalOpen(false);
      setNewLabel("");
    } catch (creationError) {
      setNewLabelError(
        getProspectErrorMessage(
          creationError,
          "The lead capture could not be saved.",
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <FormSelectField
        id="prospect-lead-capture"
        label="Lead Capture"
        value={value}
        onChange={handleSelectChange}
        options={[
          ...availableOptions.map((option) => ({
            value: option.id,
            label: option.label,
          })),
          {
            value: ADD_LEAD_CAPTURE_VALUE,
            label: (
              <span className="inline-flex rounded-md bg-cpaGold px-4 py-2 text-sm font-bold text-white">
                Add New Lead Capture
              </span>
            ),
          },
        ]}
        placeholder={
          loading ? "Loading lead captures..." : "Select Lead Capture"
        }
        error={error}
        required
        disabled={disabled || loading}
        containerClassName="mb-0 min-w-0"
      />

      <ModalShell
        open={modalOpen}
        onClose={closeModal}
        title="Add New Lead Capture"
        panelClassName="max-w-[540px]"
      >
        <form onSubmit={handleCreate} noValidate>
          <FormTextField
            id="new-lead-capture"
            label="Lead Capture Name"
            value={newLabel}
            onChange={(nextValue) => {
              setNewLabel(nextValue);
              if (newLabelError) {
                setNewLabelError("");
              }
            }}
            error={newLabelError}
            placeholder="Enter New Lead Capture"
            disabled={isSaving}
            containerClassName="mb-0 min-w-0"
          />
          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outlined"
              onClick={closeModal}
              disabled={isSaving}
              sx={{ ...wizardOutlinedButtonSx, minWidth: 96 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={
                isSaving ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{ ...wizardGoldButtonSx, minWidth: 178 }}
            >
              Save New Lead Capture
            </Button>
          </div>
        </form>
      </ModalShell>
    </>
  );
};
